import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type ReactElement,
} from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useHotkey } from '@tanstack/react-hotkeys';
import {
  ActionMenu,
  Button,
  Checkbox,
  Dialog,
  Popover,
  Tabs,
  TextArea,
  TextField,
  toastManager,
} from '@trove/ui';
import {
  CaretDown,
  CaretRight,
  DownloadSimple,
  DotsThree,
  File,
  Folder,
  GitCommit,
  Plus,
  FloppyDisk,
  ShareNetwork,
  CloudArrowUp,
} from '@phosphor-icons/react';
import {
  annotationsOf,
  filesWithin,
  isHiddenMetaPath,
  isKeepPath,
  sameAnnotation,
  type EntryAnnotation,
  type RepositoryFile,
} from '@trove/core';
import { useWorkspace } from '../workspace';
import { useSession } from '../session';
import { entriesAt, folderEntry, type FileTreeEntry } from '../fileTree';
import { AddItemSheet } from '../components/AddItemSheet';
import { FileDrawer } from '../components/FileDrawer';
import { FileGlyph, FileSheetGlyph, FolderSheetGlyph } from '../components/FileGlyph';
import { EntryAnnotationEditor } from '../components/EntryAnnotationEditor';
import { FileContentPreview } from '../components/FileContentPreview';
import { RelativeTime } from '../components/RelativeTime';
import { SetupGate } from '../components/SetupGate';
import { UploadStaging } from '../components/UploadStaging';
import { entryIconColorHex } from '../entryColors';
import { entryIconEmoji } from '../entryIcons';
import { downloadRepositoryFile, fileVisualKind, isTextEditable } from '../fileKinds';
import { formatSize } from '../formatSize';
import { folderCommitHistory } from '../folderHistory';
import { buildItemMarkdown, itemTemplate, parseTroveItem } from '../itemTemplates';
import { loadFileListDensity, type FileListDensity } from '../preferences';
import { hasFileDrag } from '../uploadFiles';
import { MOBILE_QUERY, useMediaQuery } from '../useMediaQuery';
import shared from '../styles/shared.module.css';
import styles from './files.module.css';

interface FileSearch {
  folder?: string;
  file?: string;
}

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): FileSearch => ({
    folder: typeof search.folder === 'string' ? search.folder : undefined,
    file: typeof search.file === 'string' ? search.file : undefined,
  }),
  component: RepositoryPage,
});

type Entry = FileTreeEntry;

function folderStats(files: RepositoryFile[], path: string) {
  const children = filesWithin(files, path).filter((file) => !isKeepPath(file.path));
  const latest = children.reduce<string | undefined>(
    (value, file) => (!value || file.modifiedAt > value ? file.modifiedAt : value),
    undefined,
  );
  return {
    count: children.length,
    size: children.reduce((total, file) => total + file.size, 0),
    latest,
  };
}

function allFolders(files: RepositoryFile[]): string[] {
  const folders = new Set<string>();
  for (const file of files) {
    if (isHiddenMetaPath(file.path)) continue;
    const parts = file.path.split('/');
    for (let i = 1; i < parts.length; i += 1) folders.add(parts.slice(0, i).join('/'));
  }
  return [...folders].sort();
}

function moveDestinations(files: RepositoryFile[], sourcePath: string): string[] {
  const currentParent = sourcePath.includes('/')
    ? sourcePath.slice(0, sourcePath.lastIndexOf('/'))
    : '';
  return ['', ...allFolders(files)].filter(
    (folder) =>
      folder !== sourcePath && !folder.startsWith(`${sourcePath}/`) && folder !== currentParent,
  );
}

function RepositoryPage() {
  const {
    workspace,
    loading,
    syncing,
    demoSetupError,
    saveFile,
    saveFiles,
    createFolder,
    moveEntry,
    deleteEntry,
    shareFolder,
    setEntryAnnotation,
    updateFolder,
  } = useWorkspace();
  const { user, resetDemo } = useSession();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/' });
  const dragDepth = useRef(0);
  const [newOpen, setNewOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadSeed, setUploadSeed] = useState<File[] | undefined>(undefined);
  const [uploadFolder, setUploadFolder] = useState<string | undefined>(undefined);
  const [moveEntryPath, setMoveEntryPath] = useState<string | null>(null);
  const [deleteEntryPath, setDeleteEntryPath] = useState<string | null>(null);
  const [customizeFolder, setCustomizeFolder] = useState<string | null>(null);
  const [dragUpload, setDragUpload] = useState(false);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [density] = useState<FileListDensity>(() => loadFileListDensity());
  const entries = useMemo(
    () => entriesAt(workspace?.snapshot.files ?? [], search.folder),
    [workspace?.snapshot.files, search.folder],
  );
  const annotations = workspace ? annotationsOf(workspace.snapshot) : undefined;
  const selected =
    workspace?.snapshot.files.find(
      (file) => file.path === search.file && !isHiddenMetaPath(file.path),
    ) ?? undefined;
  const folderFiles = useMemo(() => {
    const files = search.folder
      ? filesWithin(workspace?.snapshot.files ?? [], search.folder)
      : (workspace?.snapshot.files ?? []).filter((file) => !isHiddenMetaPath(file.path));
    return files.filter((file) => !isKeepPath(file.path));
  }, [workspace?.snapshot.files, search.folder]);
  const folderCommits = useMemo(
    () => folderCommitHistory(workspace?.snapshot.commits ?? [], search.folder ?? ''),
    [workspace?.snapshot.commits, search.folder],
  );
  const latestCommit = folderCommits.at(-1);

  const openEntry = (entry: Entry) => {
    if (entry.type === 'folder') void navigate({ search: { folder: entry.path } });
    else void navigate({ search: { folder: search.folder, file: entry.path } });
  };

  const startUpload = (files?: File[], targetFolder = search.folder) => {
    if (search.file) void navigate({ search: { folder: search.folder } });
    setUploadSeed(files?.length ? files : undefined);
    setUploadFolder(targetFolder);
    setUploadMode(true);
  };

  const exitUpload = () => {
    setUploadMode(false);
    setUploadSeed(undefined);
    setUploadFolder(undefined);
  };

  useHotkey('Mod+N', () => setNewOpen(true), {
    enabled: !loading && workspace?.mode === 'owner',
    meta: { name: 'Add item', description: 'Open the add-item sheet' },
  });

  useHotkey('Mod+Shift+U', () => startUpload(), {
    enabled: !loading && workspace?.mode === 'owner' && !uploadMode,
    meta: { name: 'Upload files', description: 'Open file upload' },
  });

  if (loading)
    return (
      <div className={shared.splash}>
        <div className={shared.splashInner}>
          <div className={shared.spinner} />
          Opening your trove…
        </div>
      </div>
    );
  if (user?.demo && user.displayName === 'William' && !workspace && !demoSetupError)
    return (
      <div className={shared.splash}>
        <div className={shared.splashInner}>
          <div className={shared.spinner} />
          Creating a private demo vault on this device…
        </div>
      </div>
    );
  if (demoSetupError)
    return (
      <div className={shared.splash}>
        <div className={shared.splashInner}>
          <strong>Couldn’t prepare the demo</strong>
          <span>{demoSetupError}</span>
          <Button variant="primary" onClick={() => void resetDemo()}>
            Restart demo
          </Button>
        </div>
      </div>
    );
  if (!workspace) return <SetupGate />;

  const handleDrop = async (event: ReactDragEvent, targetFolder: string) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDragUpload(false);
    setDropTarget(null);
    try {
      const sourcePath = event.dataTransfer.getData('application/x-trove-path');
      if (sourcePath) await moveEntry(sourcePath, targetFolder);
      else if (event.dataTransfer.files.length)
        startUpload(Array.from(event.dataTransfer.files), targetFolder || undefined);
    } catch (cause) {
      toastManager.add({
        title: 'Could not complete drop',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
    }
  };

  const dragOverTarget = (event: ReactDragEvent, targetFolder: string) => {
    if (workspace.mode !== 'owner') return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = hasFileDrag(event.dataTransfer) ? 'copy' : 'move';
    setDropTarget(targetFolder || '/');
  };

  const breadcrumbs = search.folder?.split('/') ?? [];
  const nested = Boolean(search.folder || search.file);
  return (
    <div className={`${styles.browser} ${nested ? styles.browserNested : ''}`} data-full-bleed>
      <section
        className={`${styles.filePane} ${nested ? styles.filePaneNested : ''} ${dragUpload && !uploadMode ? styles.filePaneDragging : ''} ${uploadMode ? styles.filePaneUpload : ''}`}
        onDragEnter={(event) => {
          if (uploadMode || workspace.mode !== 'owner' || !hasFileDrag(event.dataTransfer)) return;
          dragDepth.current += 1;
          setDragUpload(true);
        }}
        onDragOver={(event) => {
          if (uploadMode || workspace.mode !== 'owner' || !hasFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={() => {
          if (uploadMode) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (!dragDepth.current) setDragUpload(false);
        }}
        onDrop={(event) => {
          if (uploadMode) return;
          void handleDrop(event, search.folder ?? '');
        }}
      >
        <div
          className={`${shared.pageHeader} ${styles.paneHeader} ${nested ? styles.nestedHeader : ''}`}
        >
          <div>
            {search.folder && !uploadMode ? (
              workspace.mode === 'owner' ? (
                <button
                  type="button"
                  className={styles.folderTitle}
                  aria-label={`Customize ${search.folder.split('/').at(-1)}`}
                  onClick={() => setCustomizeFolder(search.folder ?? null)}
                >
                  <FileGlyph
                    entry={folderEntry(search.folder)}
                    size="row"
                    density={density}
                    iconOverride={annotations?.[search.folder]?.icon}
                    colorOverride={annotations?.[search.folder]?.color}
                  />
                  <h1>{search.folder.split('/').at(-1)}</h1>
                  <span className={styles.folderTitleMeta}>
                    {folderFiles.length} {folderFiles.length === 1 ? 'file' : 'files'}
                  </span>
                </button>
              ) : (
                <div className={styles.folderTitleStatic}>
                  <FileGlyph
                    entry={folderEntry(search.folder)}
                    size="row"
                    density={density}
                    iconOverride={annotations?.[search.folder]?.icon}
                    colorOverride={annotations?.[search.folder]?.color}
                  />
                  <h1>{search.folder.split('/').at(-1)}</h1>
                  <span className={styles.folderTitleMeta}>
                    {folderFiles.length} {folderFiles.length === 1 ? 'file' : 'files'}
                  </span>
                </div>
              )
            ) : uploadMode ? (
              <>
                <h1>Upload files</h1>
                <p>{`Drop anywhere, then set paths under ${uploadFolder ?? search.folder ?? '/'}`}</p>
              </>
            ) : (
              <div className={styles.folderTitleStatic}>
                <h1>
                  {workspace.mode === 'shared'
                    ? (workspace.sharedLabel ?? 'Shared collection')
                    : 'Personal trove'}
                </h1>
                <span className={styles.folderTitleMeta}>
                  {folderFiles.length} {folderFiles.length === 1 ? 'file' : 'files'}
                </span>
              </div>
            )}
          </div>
          <div className={shared.headerActions}>
            {workspace.mode === 'owner' && !uploadMode && (
              <>
                {search.folder ? (
                  <ShareFolderDialog
                    trigger={
                      <Button
                        variant="ghost"
                        className={styles.shareAction}
                        aria-label={`Share ${search.folder}`}
                      >
                        <ShareNetwork size={nested ? 14 : 13} />
                        <span className={styles.shareActionLabel}>Share</span>
                      </Button>
                    }
                    folder={search.folder}
                    defaultEmail={workspace.recipientEmail ?? ''}
                    onShare={shareFolder}
                  />
                ) : null}
                <Button
                  variant="primary"
                  className={nested ? styles.addFileButton : undefined}
                  aria-keyshortcuts="Meta+N Control+N"
                  onClick={() => setNewOpen(true)}
                >
                  <Plus size={nested ? 15 : 13} />
                  Add item
                </Button>
              </>
            )}
            {uploadMode && (
              <Button variant="ghost" onClick={exitUpload}>
                Done
              </Button>
            )}
          </div>
        </div>
        {uploadMode ? (
          <UploadStaging
            key={`${uploadFolder ?? ''}:${uploadSeed?.map((file) => `${file.name}:${file.size}`).join('|') ?? 'empty'}`}
            folder={uploadFolder}
            initialFiles={uploadSeed}
            syncing={syncing}
            onCancel={exitUpload}
            onUpload={async (files) => {
              await saveFiles(files);
              exitUpload();
            }}
          />
        ) : (
          <>
            {search.folder ? (
              <div className={styles.breadcrumb}>
                <button
                  className={`${styles.crumb} ${dropTarget === '/' ? styles.crumbDropTarget : ''}`}
                  onClick={() => void navigate({ search: {} })}
                  onDragOver={(event) => dragOverTarget(event, '')}
                  onDragLeave={() => setDropTarget(null)}
                  onDrop={(event) => void handleDrop(event, '')}
                >
                  Home
                </button>
                {breadcrumbs.length > 2 ? (
                  <span className={styles.crumbEllipsis} aria-hidden="true">
                    <CaretRight size={11} className={shared.subtle} />
                    <span>…</span>
                  </span>
                ) : null}
                {breadcrumbs.map((part, index) => {
                  const path = breadcrumbs.slice(0, index + 1).join('/');
                  const hiddenOnMobile = index < breadcrumbs.length - 2;
                  const current = index === breadcrumbs.length - 1;
                  return (
                    <span
                      key={path}
                      className={`${styles.crumbSegment} ${hiddenOnMobile ? styles.crumbMiddle : ''}`}
                    >
                      <CaretRight size={11} className={shared.subtle} />
                      <button
                        className={`${styles.crumb} ${current ? styles.crumbCurrent : ''} ${dropTarget === path ? styles.crumbDropTarget : ''}`}
                        title={part}
                        onClick={() => void navigate({ search: { folder: path } })}
                        onDragOver={(event) => dragOverTarget(event, path)}
                        onDragLeave={() => setDropTarget(null)}
                        onDrop={(event) => void handleDrop(event, path)}
                      >
                        {part}
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : null}
            <button
              className={styles.folderSummary}
              type="button"
              aria-label="View version history"
              onClick={() =>
                void navigate({
                  to: '/history',
                  search: search.folder ? { folder: search.folder } : {},
                })
              }
            >
              <GitCommit size={14} />
              <span className={styles.folderCommitMessage}>
                {latestCommit?.message ?? 'Encrypted repository ready'}
              </span>
              <span className={styles.folderCommitMeta}>
                {latestCommit && (
                  <>
                    {latestCommit.author} · <RelativeTime value={latestCommit.createdAt} />
                  </>
                )}
              </span>
              <span className={styles.folderSummaryStats}>
                {entries.length} direct items · {folderFiles.length} files total
              </span>
            </button>
            <table className={styles.fileTable} data-density={density}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Modified</th>
                  {workspace.mode === 'owner' && <th className={styles.fileActionsHead} />}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const stats =
                    entry.type === 'folder'
                      ? folderStats(workspace.snapshot.files, entry.path)
                      : undefined;
                  return (
                    <tr
                      key={entry.path}
                      draggable={workspace.mode === 'owner'}
                      className={`${styles.fileRow} ${search.file === entry.path ? styles.fileRowSelected : ''} ${dropTarget === entry.path ? styles.fileRowDropTarget : ''}`}
                      onClick={() => openEntry(entry)}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('application/x-trove-path', entry.path);
                        event.dataTransfer.setData('text/plain', entry.path);
                      }}
                      onDragEnd={() => {
                        setDropTarget(null);
                        setDragUpload(false);
                        dragDepth.current = 0;
                      }}
                      onDragOver={
                        entry.type === 'folder'
                          ? (event) => dragOverTarget(event, entry.path)
                          : undefined
                      }
                      onDragLeave={entry.type === 'folder' ? () => setDropTarget(null) : undefined}
                      onDrop={
                        entry.type === 'folder'
                          ? (event) => void handleDrop(event, entry.path)
                          : undefined
                      }
                    >
                      <td>
                        <span className={styles.fileName}>
                          <FileGlyph
                            entry={entry}
                            size="row"
                            density={density}
                            iconOverride={annotations?.[entry.path]?.icon}
                            colorOverride={annotations?.[entry.path]?.color}
                          />
                          <span title={entry.name}>
                            {entry.name}
                            {annotations?.[entry.path]?.tags?.length ? (
                              <span className={styles.fileNameMeta}>
                                {annotations[entry.path]!.tags!.join(' · ')}
                              </span>
                            ) : null}
                            {stats && (
                              <span className={styles.fileNameMeta}>
                                {stats.count} {stats.count === 1 ? 'file' : 'files'}
                              </span>
                            )}
                          </span>
                        </span>
                      </td>
                      <td className={styles.fileMeta}>
                        {entry.type === 'file'
                          ? formatSize(entry.file.size)
                          : formatSize(stats!.size)}
                      </td>
                      <td className={styles.fileMeta}>
                        {entry.type === 'file' ? (
                          <RelativeTime value={entry.file.modifiedAt} />
                        ) : stats?.latest ? (
                          <RelativeTime value={stats.latest} />
                        ) : (
                          '—'
                        )}
                      </td>
                      {workspace.mode === 'owner' && (
                        <td className={styles.fileActions}>
                          <div
                            className={styles.fileActionsMenu}
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <ActionMenu
                              trigger={
                                <Button
                                  iconOnly
                                  size="small"
                                  variant="ghost"
                                  aria-label={`Actions for ${entry.name}`}
                                >
                                  <DotsThree size={14} />
                                </Button>
                              }
                              items={[
                                {
                                  label: 'Customize…',
                                  onClick: () => {
                                    if (entry.type === 'folder') setCustomizeFolder(entry.path);
                                    else
                                      void navigate({
                                        search: { folder: search.folder, file: entry.path },
                                      });
                                  },
                                },
                                { label: 'Move…', onClick: () => setMoveEntryPath(entry.path) },
                                { label: 'actions-separator', onClick: () => {}, separator: true },
                                {
                                  label: 'Delete',
                                  onClick: () => setDeleteEntryPath(entry.path),
                                  danger: true,
                                },
                              ]}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!entries.length &&
              (workspace.mode === 'owner' ? (
                <button
                  type="button"
                  className={styles.emptyDropzone}
                  onClick={() => setUploadMode(true)}
                >
                  <CloudArrowUp size={22} />
                  <strong>Drop files here</strong>
                  <span>Or choose files and set custom paths</span>
                </button>
              ) : (
                <div className={shared.empty}>This folder is empty.</div>
              ))}
            {dragUpload && (
              <div className={styles.uploadDropOverlay}>
                <div className={styles.uploadDropCard}>
                  <CloudArrowUp size={28} />
                  <strong>Drop to stage upload</strong>
                  <span>Set custom paths before encrypting into {search.folder ?? '/'}.</span>
                </div>
              </div>
            )}
          </>
        )}
      </section>
      {selected && (
        <FileSheet
          file={selected}
          annotation={annotations?.[selected.path]}
          readOnly={workspace.mode === 'shared'}
          syncing={syncing}
          onClose={() => void navigate({ search: { folder: search.folder } })}
          onSave={(content, nextAnnotation) =>
            saveFile(selected.path, content, selected.mimeType, nextAnnotation)
          }
          onAnnotate={(next) => setEntryAnnotation(selected.path, next)}
        />
      )}
      <AddItemSheet
        open={newOpen}
        onOpenChange={setNewOpen}
        folder={search.folder}
        onCreate={saveFile}
        onCreateFolder={async (path, annotation) => {
          await createFolder(path, annotation);
          void navigate({ search: { folder: path } });
        }}
        onUpload={(files) => startUpload(files)}
      />
      <MoveEntryDialog
        open={Boolean(moveEntryPath)}
        path={moveEntryPath}
        destinations={
          moveEntryPath ? moveDestinations(workspace.snapshot.files, moveEntryPath) : []
        }
        syncing={syncing}
        onOpenChange={(open) => {
          if (!open) setMoveEntryPath(null);
        }}
        onMove={async (targetFolder) => {
          if (!moveEntryPath) return;
          const source = moveEntryPath;
          await moveEntry(source, targetFolder);
          setMoveEntryPath(null);
          if (search.file === source || search.file?.startsWith(`${source}/`)) {
            void navigate({ search: { folder: search.folder } });
          } else if (search.folder === source || search.folder?.startsWith(`${source}/`)) {
            void navigate({ search: { folder: targetFolder || undefined } });
          }
        }}
      />
      <DeleteEntryDialog
        open={Boolean(deleteEntryPath)}
        path={deleteEntryPath}
        syncing={syncing}
        onOpenChange={(open) => {
          if (!open) setDeleteEntryPath(null);
        }}
        onDelete={async () => {
          if (!deleteEntryPath) return;
          const source = deleteEntryPath;
          await deleteEntry(source);
          setDeleteEntryPath(null);
          if (search.file === source || search.file?.startsWith(`${source}/`)) {
            void navigate({ search: { folder: search.folder } });
          } else if (search.folder === source || search.folder?.startsWith(`${source}/`)) {
            void navigate({ search: {} });
          }
        }}
      />
      {customizeFolder && (
        <FolderSheet
          key={customizeFolder}
          path={customizeFolder}
          annotation={annotations?.[customizeFolder]}
          stats={folderStats(workspace.snapshot.files, customizeFolder)}
          syncing={syncing}
          onClose={() => setCustomizeFolder(null)}
          onSave={async (nextName, next) => {
            const nextPath = await updateFolder(customizeFolder, {
              name: nextName,
              annotation: next,
            });
            setCustomizeFolder(null);
            if (nextPath === customizeFolder) return;
            const remap = (value?: string) => {
              if (!value) return value;
              if (value === customizeFolder) return nextPath;
              if (value.startsWith(`${customizeFolder}/`)) {
                return `${nextPath}${value.slice(customizeFolder.length)}`;
              }
              return value;
            };
            void navigate({
              search: {
                folder: remap(search.folder),
                file: remap(search.file),
              },
            });
          }}
        />
      )}
    </div>
  );
}

function FileSheet({
  file,
  annotation,
  readOnly,
  syncing,
  onClose,
  onSave,
  onAnnotate,
}: {
  file: RepositoryFile;
  annotation?: EntryAnnotation;
  readOnly: boolean;
  syncing: boolean;
  onClose: () => void;
  onSave: (content: string, annotation?: EntryAnnotation | null) => Promise<void>;
  onAnnotate: (next: EntryAnnotation | null) => Promise<void>;
}) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isPdf = fileVisualKind(file) === 'pdf';
  const troveItem = useMemo(() => parseTroveItem(file.content ?? ''), [file.content]);
  const troveTemplate = troveItem ? itemTemplate(troveItem.kind) : null;
  const [content, setContent] = useState(file.content ?? '');
  const [fields, setFields] = useState<Record<string, string>>(troveItem?.values ?? {});
  const [annotationDraft, setAnnotationDraft] = useState<EntryAnnotation | null>(
    annotation ?? null,
  );
  const [annotateOpen, setAnnotateOpen] = useState(false);
  useEffect(() => {
    setContent(file.content ?? '');
    const parsed = parseTroveItem(file.content ?? '');
    setFields(parsed?.values ?? {});
  }, [file.content]);
  useEffect(() => setAnnotationDraft(annotation ?? null), [annotation]);
  const annotationDirty = !sameAnnotation(annotationDraft, annotation);
  const nextContent =
    troveItem && troveTemplate
      ? buildItemMarkdown(troveItem.kind, fields, troveTemplate.fields)
      : content;
  const contentDirty = nextContent !== file.content;
  const draftEmoji = entryIconEmoji(annotationDraft?.icon);
  const draftColor = entryIconColorHex(annotationDraft?.color);
  const annotateLabel = annotationDraft?.tags?.length
    ? annotationDraft.tags.join(' · ')
    : annotationDraft?.icon || annotationDraft?.color
      ? 'Annotated'
      : 'Annotate';
  const previewFile = contentDirty ? { ...file, content: nextContent } : file;
  const preview = <FileContentPreview file={previewFile} />;

  const title = troveItem
    ? troveItem.title || file.path.split('/').at(-1) || file.path
    : (file.path.split('/').at(-1) ?? file.path);
  const parent = file.path.includes('/')
    ? file.path.slice(0, file.path.lastIndexOf('/'))
    : undefined;
  const description = troveItem
    ? [troveTemplate?.label ?? troveItem.kind, parent, formatSize(file.size)]
        .filter(Boolean)
        .join(' · ')
    : [parent, formatSize(file.size)].filter(Boolean).join(' · ');
  const saveLabel =
    annotationDirty && contentDirty
      ? 'Save'
      : annotationDirty
        ? 'Save annotations'
        : troveItem
          ? 'Save item'
          : 'Save snapshot';

  const headerActions = (
    <>
      {!readOnly && (
        <Popover
          open={annotateOpen}
          onOpenChange={setAnnotateOpen}
          trigger={
            <Button
              size="small"
              variant="ghost"
              disabled={syncing}
              aria-label="Edit annotations"
              className={styles.annotateTrigger}
            >
              {draftEmoji ? (
                <span
                  className={styles.annotateEmoji}
                  style={draftColor ? { backgroundColor: draftColor } : undefined}
                  aria-hidden="true"
                >
                  {draftEmoji}
                </span>
              ) : (
                <File size={14} style={draftColor ? { color: draftColor } : undefined} />
              )}
              <span className={styles.annotateTriggerLabel}>{annotateLabel}</span>
              <CaretDown size={12} />
            </Button>
          }
        >
          <div className={styles.annotatePopover}>
            <EntryAnnotationEditor
              value={annotationDraft ?? undefined}
              disabled={syncing}
              defaultIcon={<File size={15} />}
              onChange={setAnnotationDraft}
            />
          </div>
        </Popover>
      )}
      {!readOnly && (annotationDirty || contentDirty) && (
        <Button
          size="small"
          variant="primary"
          disabled={syncing}
          onClick={() => {
            void (async () => {
              if (troveItem) {
                await onSave(nextContent, annotationDraft);
                return;
              }
              if (contentDirty) await onSave(nextContent);
              if (annotationDirty) await onAnnotate(annotationDraft);
            })();
          }}
        >
          <FloppyDisk size={13} />
          {saveLabel}
        </Button>
      )}
      <Button
        iconOnly
        size="small"
        variant="ghost"
        aria-label="Download"
        onClick={() => downloadRepositoryFile(file)}
      >
        <DownloadSimple size={13} />
      </Button>
    </>
  );

  const titleIcon = (
    <FileSheetGlyph
      file={file}
      iconOverride={annotationDraft?.icon}
      colorOverride={annotationDraft?.color}
    />
  );

  const body = (
    <div className={`${styles.fileSheetBody} ${isPdf ? styles.fileSheetBodyPdf : ''}`}>
      {readOnly && (annotation?.icon || annotation?.color || annotation?.tags?.length) ? (
        <div className={styles.annotateBar}>
          <span className={styles.annotateTriggerLabel}>
            {annotation.tags?.length ? annotation.tags.join(' · ') : 'Annotated'}
          </span>
        </div>
      ) : null}
      {troveItem && troveTemplate ? (
        <Tabs
          defaultValue="item"
          tabs={[
            {
              value: 'item',
              label: troveTemplate.label,
              content: (
                <div className={`${shared.formStack} ${styles.fileSheetInset}`}>
                  {troveTemplate.fields.map((field) =>
                    field.kind === 'multiline' ? (
                      <TextArea
                        key={field.key}
                        label={field.label}
                        value={fields[field.key] ?? ''}
                        placeholder={field.placeholder}
                        disabled={readOnly || syncing}
                        onChange={(event) =>
                          setFields((current) => ({
                            ...current,
                            [field.key]: event.target.value,
                          }))
                        }
                      />
                    ) : (
                      <TextField
                        key={field.key}
                        label={field.label}
                        type={field.kind === 'secret' ? 'password' : 'text'}
                        placeholder={field.placeholder}
                        value={fields[field.key] ?? ''}
                        disabled={readOnly || syncing}
                        onValueChange={(value) =>
                          setFields((current) => ({ ...current, [field.key]: value }))
                        }
                      />
                    ),
                  )}
                </div>
              ),
            },
            {
              value: 'markdown',
              label: 'Markdown',
              content: <div className={styles.fileSheetPreview}>{preview}</div>,
            },
          ]}
        />
      ) : (
        <Tabs
          defaultValue="preview"
          tabs={[
            {
              value: 'preview',
              label: 'Preview',
              content: <div className={styles.fileSheetPreview}>{preview}</div>,
            },
            ...(!readOnly && isTextEditable(file)
              ? [
                  {
                    value: 'edit',
                    label: 'Edit',
                    content: (
                      <div className={`${shared.formStack} ${styles.fileSheetInset}`}>
                        <TextArea
                          label="Contents"
                          value={content}
                          onChange={(event) => setContent(event.target.value)}
                        />
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <FileDrawer
        title={title}
        description={description}
        titleIcon={titleIcon}
        headerActions={headerActions}
        onClose={onClose}
        initialSnap={isPdf ? 'full' : 'half'}
      >
        {body}
      </FileDrawer>
    );
  }

  return (
    <Dialog
      presentation="sheet"
      className={isPdf ? styles.pdfSheetDialog : undefined}
      bodyClassName={isPdf ? styles.pdfSheetDialogBody : undefined}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      description={description}
      titleIcon={titleIcon}
      headerActions={headerActions}
    >
      {body}
    </Dialog>
  );
}

function FolderSheet({
  path,
  annotation,
  stats,
  syncing,
  onClose,
  onSave,
}: {
  path: string;
  annotation?: EntryAnnotation;
  stats: { count: number; size: number; latest?: string };
  syncing: boolean;
  onClose: () => void;
  onSave: (name: string, next: EntryAnnotation | null) => Promise<void>;
}) {
  const name = path.split('/').at(-1) ?? path;
  const [draft, setDraft] = useState<EntryAnnotation | null>(annotation ?? null);
  const [nameDraft, setNameDraft] = useState(name);
  useEffect(() => {
    setDraft(annotation ?? null);
    setNameDraft(name);
  }, [annotation, name, path]);
  const trimmedName = nameDraft.trim();
  const dirty = !sameAnnotation(draft, annotation) || trimmedName !== name;
  const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : undefined;
  const titleIcon = (
    <FolderSheetGlyph path={path} iconOverride={draft?.icon} colorOverride={draft?.color} />
  );

  return (
    <Dialog
      presentation="sheet"
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={trimmedName || name}
      description={
        [parent, `${stats.count} ${stats.count === 1 ? 'file' : 'files'}`, formatSize(stats.size)]
          .filter(Boolean)
          .join(' · ') || undefined
      }
      titleIcon={titleIcon}
      headerActions={
        dirty ? (
          <Button
            size="small"
            variant="primary"
            disabled={syncing || !trimmedName || trimmedName.includes('/')}
            onClick={() => void onSave(trimmedName, draft)}
          >
            <FloppyDisk size={13} />
            Save
          </Button>
        ) : undefined
      }
    >
      {stats.latest ? (
        <div className={styles.folderSheetMeta}>
          Updated <RelativeTime value={stats.latest} />
        </div>
      ) : null}
      <div className={styles.folderSheetName}>
        <TextField label="Name" value={nameDraft} disabled={syncing} onValueChange={setNameDraft} />
      </div>
      <EntryAnnotationEditor
        value={draft ?? undefined}
        disabled={syncing}
        defaultIcon={<Folder size={15} />}
        onChange={setDraft}
      />
    </Dialog>
  );
}

function ShareFolderDialog({
  trigger,
  folder,
  defaultEmail,
  onShare,
}: {
  trigger: ReactElement;
  folder: string;
  defaultEmail: string;
  onShare: (
    folder: string,
    email: string,
    options: {
      expiresAt: string | null;
      maxViews?: 1 | null;
      accessCode?: string | null;
    },
  ) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    defaultValues: {
      email: defaultEmail,
      access: '7d' as ShareAccessOption,
      requireCode: false,
      magicCode: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const access = SHARE_ACCESS_OPTIONS.find((option) => option.value === value.access);
        await onShare(folder, value.email, {
          expiresAt: access?.days
            ? new Date(Date.now() + access.days * 86_400_000).toISOString()
            : null,
          maxViews: access?.once ? 1 : null,
          accessCode: value.requireCode ? value.magicCode : null,
        });
        setOpen(false);
      } catch (cause) {
        toastManager.add({
          title: 'Could not share folder',
          description: cause instanceof Error ? cause.message : 'Try again.',
        });
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      email: defaultEmail,
      access: '7d',
      requireCode: false,
      magicCode: '',
    });
  }, [open, defaultEmail]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when dialog opens

  return (
    <Dialog
      trigger={trigger}
      open={open}
      onOpenChange={setOpen}
      title={`Share ${folder}`}
      description="Only this folder becomes an independently keyed collection."
      actions={
        <>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <form.Subscribe selector={(state) => [state.values.email, state.isSubmitting] as const}>
            {([email, isSubmitting]) => (
              <Button
                variant="primary"
                disabled={!/\S+@\S+\.\S+/.test(email) || isSubmitting}
                onClick={() => void form.handleSubmit()}
              >
                <ShareNetwork size={13} />
                {isSubmitting ? 'Sharing…' : 'Share read-only'}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form.Field
        name="email"
        validators={{
          onChange: ({ value }) => (/\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email'),
          onBlur: ({ value }) => (/\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email'),
        }}
      >
        {(field) => (
          <TextField
            label="Recipient email"
            type="email"
            value={field.state.value}
            onValueChange={field.handleChange}
            error={field.state.meta.errors.join(', ')}
          />
        )}
      </form.Field>
      <form.Field name="access">
        {(field) => (
          <label className={styles.moveField}>
            <span className={styles.moveLabel}>Access</span>
            <select
              className={styles.moveSelect}
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value as ShareAccessOption)}
            >
              {SHARE_ACCESS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </form.Field>
      <form.Field name="requireCode">
        {(requireField) => (
          <form.Field name="magicCode">
            {(codeField) => (
              <div className={styles.sharePrivate}>
                <Checkbox
                  label="Require magic code"
                  checked={requireField.state.value}
                  onCheckedChange={(checked) => {
                    const on = checked === true;
                    requireField.handleChange(on);
                    codeField.handleChange(on ? createMagicCode() : '');
                  }}
                />
                {requireField.state.value ? (
                  <div className={styles.magicCodeRow}>
                    <code className={styles.magicCode}>{codeField.state.value}</code>
                    <Button
                      size="small"
                      onClick={() => {
                        void navigator.clipboard.writeText(codeField.state.value);
                        toastManager.add({ title: 'Magic code copied' });
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </form.Field>
        )}
      </form.Field>
    </Dialog>
  );
}

type ShareAccessOption = 'once' | '1d' | '3d' | '7d' | '30d';

const SHARE_ACCESS_OPTIONS: Array<{
  value: ShareAccessOption;
  label: string;
  days: number | null;
  once?: boolean;
}> = [
  { value: 'once', label: 'View once', days: null, once: true },
  { value: '1d', label: 'Expires in 1 day', days: 1 },
  { value: '3d', label: 'Expires in 3 days', days: 3 },
  { value: '7d', label: 'Expires in 7 days', days: 7 },
  { value: '30d', label: 'Expires in 30 days', days: 30 },
];

function createMagicCode() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  const value = [...bytes].map((byte) => alphabet[byte % alphabet.length]).join('');
  return `${value.slice(0, 4)}-${value.slice(4)}`;
}

function MoveEntryDialog({
  open,
  path,
  destinations,
  syncing,
  onOpenChange,
  onMove,
}: {
  open: boolean;
  path: string | null;
  destinations: string[];
  syncing: boolean;
  onOpenChange: (open: boolean) => void;
  onMove: (targetFolder: string) => Promise<void>;
}) {
  const [target, setTarget] = useState('');
  const defaultTarget = destinations[0] ?? '';
  useEffect(() => {
    if (open) setTarget(defaultTarget);
  }, [open, path, defaultTarget]);
  if (!path) return null;
  const name = path.split('/').at(-1) ?? path;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Move ${name}`}
      description="Choose a destination folder for this encrypted snapshot."
      actions={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={syncing || !destinations.length}
            onClick={() => void onMove(target)}
          >
            Move
          </Button>
        </>
      }
    >
      <label className={styles.moveField}>
        <span className={styles.moveLabel}>Destination</span>
        <select
          className={styles.moveSelect}
          value={target}
          onChange={(event) => setTarget(event.target.value)}
        >
          {destinations.map((folder) => (
            <option key={folder || '/'} value={folder}>
              {folder || '/'}
            </option>
          ))}
        </select>
      </label>
    </Dialog>
  );
}

function DeleteEntryDialog({
  open,
  path,
  syncing,
  onOpenChange,
  onDelete,
}: {
  open: boolean;
  path: string | null;
  syncing: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => Promise<void>;
}) {
  if (!path) return null;
  const name = path.split('/').at(-1) ?? path;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Delete ${name}?`}
      description="This creates a new signed snapshot without the selected item."
      actions={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="danger" disabled={syncing} onClick={() => void onDelete()}>
            Delete
          </Button>
        </>
      }
    >
      <div className={shared.info}>
        <code>{path}</code> will be removed from future snapshots. Prior history remains available.
      </div>
    </Dialog>
  );
}
